<template>
  <div>
    <v-card :disabled="isProcessing" :loading="isProcessing">
      <template v-slot:progress>
        <v-progress-linear absolute color="success" height="4" indeterminate></v-progress-linear>
      </template>
      <v-tabs v-model="tab" class="stock-color" left dark icons-and-text>
        <v-tabs-slider></v-tabs-slider>
        <v-tab href="#tab-1">
          <v-toolbar-title>{{ title }}</v-toolbar-title>
        </v-tab>
      </v-tabs>
      <v-tabs-items v-model="tab">
        <v-tab-item value="tab-1">
          <v-card flat>
            <v-card-text>
              <v-alert v-if="isKilogram" type="info" dense outlined class="mb-4">
                {{ $t("STOCK.UNITS.KILOGRAM_HINT") }}
              </v-alert>
              <v-alert v-else-if="isSystem" type="info" dense outlined class="mb-4">
                {{ $t("STOCK.UNITS.SYSTEM_HINT") }}
              </v-alert>
              <v-form ref="unitForm" v-model="validForm" @submit.prevent>
                <v-row>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="requestData.code"
                      :label="$t('GENERAL.LABELS.CODE') + '*'"
                      :disabled="isSystem"
                      :rules="[validations.required()]"
                      :error-messages="validationErrors.code || []"
                      @change="clearValidationError('code')"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="requestData.name"
                      :label="$t('GENERAL.LABELS.NAME') + '*'"
                      :rules="[validations.required()]"
                      :error-messages="validationErrors.name || []"
                      @change="clearValidationError('name')"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="requestData.code_bg"
                      :label="$t('STOCK.UNITS.CODE_BG') + '*'"
                      :rules="[validations.required()]"
                      :error-messages="validationErrors.code_bg || []"
                      @change="clearValidationError('code_bg')"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="requestData.name_bg"
                      :label="$t('STOCK.UNITS.NAME_BG') + '*'"
                      :rules="[validations.required()]"
                      :error-messages="validationErrors.name_bg || []"
                      @change="clearValidationError('name_bg')"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model.number="requestData.decimals"
                      type="number"
                      min="0"
                      max="6"
                      step="1"
                      :label="$t('STOCK.UNITS.DECIMALS') + '*'"
                      :rules="[decimalsRule]"
                      :error-messages="validationErrors.decimals || []"
                      @change="clearValidationError('decimals')"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-checkbox
                      v-model="requestData.is_active"
                      class="mt-0"
                      :disabled="isKilogram"
                      :label="$t('GENERAL.LABELS.ACTIVE')"
                      color="primary"
                      hide-details
                    ></v-checkbox>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="warning" @click="back">
                {{ $t("GENERAL.LABELS.BACK_BUTTON") }}
              </v-btn>
              <v-btn color="success" @click="store">
                {{ actionText }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </div>
</template>

<script>
import validations from "@/views/partials/form/Validations.vue";
import userStateService from "@/common/userState.service";
import { SHOW_SNACK } from "@/store/snack.module";
import {
  CREATE_STOCK_UNIT,
  GET_STOCK_UNIT,
  UPDATE_STOCK_UNIT,
} from "@/store/stock/stockUnits.module";

export default {
  name: "StockEditUnit",
  data() {
    return {
      validations,
      tab: "tab-1",
      isProcessing: false,
      validForm: false,
      isSystem: false,
      isKilogram: false,
      validationErrors: {},
      title: this.$t("STOCK.UNITS.CREATE"),
      actionText: this.$t("GENERAL.LABELS.CREATE_BUTTON"),
      requestData: {
        code: "",
        name: "",
        name_bg: "",
        code_bg: "",
        decimals: 0,
        is_active: true,
      },
    };
  },
  mounted() {
    this.loadUnit();
  },
  methods: {
    decimalsRule(value) {
      if (value === 0 || value === "0") {
        return true;
      }
      if (value === null || value === undefined || value === "") {
        return "Item is required";
      }
      const number = Number(value);
      if (!Number.isInteger(number) || number < 0 || number > 6) {
        return this.$t("STOCK.UNITS.DECIMALS_RANGE");
      }
      return true;
    },
    getErrorMessage(error) {
      if (error && error.errors && typeof error.errors === "object") {
        const firstErrorField = Object.keys(error.errors)[0];
        const firstErrorList = error.errors[firstErrorField];
        if (Array.isArray(firstErrorList) && firstErrorList.length) {
          return firstErrorList[0];
        }
      }
      return error?.message || this.$t("GENERAL.UNEXPECTED_ERROR");
    },
    clearValidationError(field) {
      if (this.validationErrors[field]) {
        this.$delete(this.validationErrors, field);
      }
    },
    loadUnit() {
      if (!this.$route.params.id) {
        return;
      }

      this.title = this.$t("STOCK.UNITS.EDIT");
      this.actionText = this.$t("GENERAL.LABELS.SAVE");
      this.isProcessing = true;

      return this.$store
        .dispatch(GET_STOCK_UNIT, this.$route.params.id)
        .then((response) => {
          this.isSystem = !!response.is_system;
          this.isKilogram = !!response.is_kilogram;
          this.requestData = {
            code: response.code || "",
            name: response.name || "",
            name_bg: response.name_bg || "",
            code_bg: response.code_bg || "",
            decimals: response.decimals ?? 0,
            is_active: !!response.is_active,
          };
        })
        .finally(() => {
          this.isProcessing = false;
        });
    },
    back() {
      this.$router.push({ name: "stock-units" });
    },
    store() {
      this.$refs.unitForm.validate();
      if (!this.validForm) {
        return;
      }

      this.validationErrors = {};
      let action = CREATE_STOCK_UNIT;
      let payload = this.requestData;

      if (this.$route.params.id) {
        action = UPDATE_STOCK_UNIT;
        payload = {
          id: this.$route.params.id,
          params: this.requestData,
        };
      }

      this.isProcessing = true;
      this.$store
        .dispatch(action, payload)
        .then((response) => {
          const blinkId = response?.id || this.$route.params.id;
          if (blinkId) {
            userStateService.setState("table-state.stock-units-table.blinkRowItemKey", blinkId);
          }
          this.$store.dispatch(SHOW_SNACK, { message: this.$t("GENERAL.SUCCESS_MESSAGE") });
          this.$router.push({ name: "stock-units" });
        })
        .catch((error) => {
          this.validationErrors = error?.errors || {};
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        })
        .finally(() => {
          this.isProcessing = false;
        });
    },
  },
};
</script>
